import React, { useCallback, useEffect, useState } from "react";
import "../assets/styles/components/generic-chatbot.scss";
import EmailInputForm from "./forms/EmailInputForm";
import ChatBot from "./ChatBot";
import useEventEmitter from "../hooks/useEventEmitter";
import { createConversation } from "../api/services/conversations";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../states";
import {
  resetHistory,
  setConversationId,
  updateDoabilityBot,
  updateStyleBot,
} from "../states/slices/chatSlice";

interface GenericChatbotProps {
  onDiscoverClick: () => void;
  showHgChatbot: boolean;
}

const GenericChatbot: React.FC<GenericChatbotProps> = ({
  onDiscoverClick,
  showHgChatbot,
}) => {
  const dispatch = useDispatch();
  const doabilityBot = useSelector(
    (state: RootState) => state.chat.doabilityBot
  );
  const styleRecommenderBot = useSelector(
    (state: RootState) => state.chat.styleBot
  );
  const email = useSelector((state: RootState) => state.user.email);
  const conversationId = useSelector(
    (state: RootState) => state.chat.conversationId
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { onEvent, offEvent } = useEventEmitter();

  const disableDoabilityBot = (key: string) => {
    dispatch(
      updateDoabilityBot({
        isActive: key === "NotDoable",
        key: key,
        type: "doability",
      })
    );
  };

  const disableStyleRecommenderBot = (key: string) => {
    dispatch(updateStyleBot({ isActive: false, key: key, type: "style" }));
  };

  const resetBots = useCallback(() => {
    createConversation({ email: email })
      .then((resp) => {
        dispatch(setConversationId(resp.id));
        dispatch(
          updateDoabilityBot({ isActive: true, key: "", type: "doability" })
        );
        dispatch(updateStyleBot({ isActive: true, key: "", type: "style" }));
        dispatch(resetHistory());
      })
      .catch(() => {
        toast.error("Something went wrong, please try again later.");
      });
  }, [dispatch, email]);

  useEffect(() => {
    onEvent("anotherRecommendation", resetBots);

    return () => {
      offEvent("anotherRecommendation", resetBots);
    };
  }, [offEvent, onEvent, resetBots]);

  useEffect(() => {
    if (email && conversationId) {
      setIsSubmitted(true);
    }
  }, [email, conversationId]);

  return (
    <div className="chatbot-container">
      <div className="form-container">
        <img
          alt="Recommender Banner"
          src="https://hg-recommender-cards.s3.ap-southeast-1.amazonaws.com/Banner/HGDTB+4.png"
        />
        <p className="description">
          Answer a few questions (about 5 mins) and we’ll help you find out if
          homeschooling is doable for you and what your unique{" "}
          <strong>homeschooling style</strong> is. Enjoy!
        </p>
        {!isSubmitted && (
          <EmailInputForm onSubmit={() => setIsSubmitted(true)} />
        )}
      </div>
      {isSubmitted && conversationId && (
        <>
          <ChatBot
            chatBot={doabilityBot}
            onChatBotDisable={disableDoabilityBot}
          />
          {doabilityBot.key && doabilityBot.key === "Doable" && (
            <ChatBot
              chatBot={styleRecommenderBot}
              onChatBotDisable={disableStyleRecommenderBot}
            />
          )}

          <div className="buttons">
            {doabilityBot.key === "Doable" && styleRecommenderBot.key && (
              <button
                className="discover-program-btn"
                onClick={onDiscoverClick}
              >
                Let's find out your Homeschool Global program!
                <i
                  className={`fa-solid ${
                    showHgChatbot ? "fa-angle-down" : "fa-angle-right"
                  }`}
                ></i>
              </button>
            )}
            {doabilityBot.key === "NotDoable" && (
              <a
                href="https://vcis.edu.ph/index.php/vcis-online/"
                className="discover-program-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                Go to VCIS Online
                <i className="fa-solid fa-angle-right"></i>
              </a>
            )}
            <button className="get-reco-btn" onClick={resetBots}>
              Get another recommendation
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default GenericChatbot;
